<template>
  <div class="realtimeplot-container">
    <!-- 连接状态提示区域 -->
    <div v-if="isReconnecting" class="connection-status">
      <el-alert
        :title="reconnectMessage"
        type="warning"
        :closable="false"
        show-icon
      >
      </el-alert>
    </div>

    <!-- 图表区域 -->
    <div class="chart-container">
      <div ref="chartRef" class="chart"></div>
      
      <!-- 轴范围控制工具栏 -->
      <div class="chart-toolbar">
        <el-dropdown @command="handleAxisCommand" trigger="click">
          <el-button size="small" type="primary" plain>
            {{ $t('message.historyRecord.yAxisControl') }} 
            <el-icon class="el-icon--right"><arrow-down /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="showYAxisSettings">
                {{ $t('message.historyRecord.showAllYAxisSettings') }}
              </el-dropdown-item>
              <el-dropdown-item divided command="clearAllYAxis">
                {{ $t('message.historyRecord.clearAllYAxisRanges') }}
              </el-dropdown-item>
              <el-dropdown-item command="showXAxisControl" divided>
                {{ $t('message.historyRecord.showAllXAxisSettings') }}                
              </el-dropdown-item>
              <el-dropdown-item command="clearXAxisRange" divided>
                {{ $t('message.historyRecord.clearAllXAxisRanges') }}                
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
    </div>

    <!-- 筛选条件区域 - 紧凑一行布局 -->
    <div class="control-panel">
      <div class="filter-row">
        <!-- 监控时长 -->
        <span class="label-compact">{{ $t('message.reaTimePlot.MonitoringDuration') }}</span>
        <el-input-number 
          v-model="monitoringDuration" 
          :min="12" 
          size="default" 
          controls-position="right" 
          style="width: 100px" 
        />
        
        <!-- 点名 -->
        <el-button type="primary" size="default" @click="rollCallClick">
          {{ $t("message.reaTimePlot.rollCall") }}
        </el-button>
        
        <!-- 追踪 -->
        <el-button 
          type="primary" 
          size="default"
          :icon="Refresh" 
          :loading="partDataIsRollCallLoading"
          :disabled="!isConnected"
          @click="handleTrack"
        >
          {{ $t("message.reaTimePlot.track") }}
        </el-button>
        
        <!-- 停止追踪 -->
        <el-button 
          v-if="isTracking"
          type="danger" 
          size="default"
          @click="handleStopTrack"
        >
          <el-icon><SwitchButton /></el-icon>
          {{ t("message.ClientProjectMonitorSystemMonitorParmater.stopTrack") }}
        </el-button>

        <!-- 实时数据开关 -->
        <el-button 
          :type="isDataReceiving ? 'success' : 'warning'"
          size="default"
          @click="toggleDataReceiving"
          plain
        >
          <el-icon>
            <VideoCamera v-if="isDataReceiving" />
            <VideoPause v-else />
          </el-icon>
          {{ isDataReceiving ? t('message.reaTimePlot.stopReceiving') : t('message.reaTimePlot.startReceiving') }}
        </el-button>
        
        <!-- 状态标签 -->
        <el-tag 
          :type="isDataReceiving ? 'success' : 'info'" 
          size="default"
          effect="plain"
        >
          {{ isDataReceiving ? t('message.reaTimePlot.receiving') : t('message.reaTimePlot.paused') }}
        </el-tag>
      </div>
    </div>

    <!-- 追踪对话框 -->
    <el-dialog
      v-model="TrackDialogVisible"
      :title="$t('message.ClientProjectMonitorSystemMonitorParmater.track')"
      width="500px"
    >
      <div class="edit-dialog-content">
        <el-form :model="trackForm" label-width="140px">
          <el-form-item :label="$t('message.ClientProjectMonitorSystemMonitorParmater.updateMode')">
            <el-radio-group v-model="trackForm.updateMode">
              <el-radio value="auto">
                {{ $t('message.ClientProjectMonitorSystemMonitorParmater.autoUpdate') }}
              </el-radio>
              <el-radio value="manual">
                {{ $t('message.ClientProjectMonitorSystemMonitorParmater.manualUpdate') }}
              </el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item 
            v-if="trackForm.updateMode === 'manual'" 
            :label="$t('message.ClientProjectMonitorSystemMonitorParmater.updateInterval')"
          >
            <el-radio-group v-model="trackForm.manualInterval">
              <el-radio :label="1">{{ $t('message.ClientProjectMonitorSystemMonitorParmater.oneSecond') }}</el-radio>
              <el-radio :label="5">{{ $t('message.ClientProjectMonitorSystemMonitorParmater.fiveSeconds') }}</el-radio>
              <el-radio :label="10">{{ $t('message.ClientProjectMonitorSystemMonitorParmater.tenSeconds') }}</el-radio>
              <el-radio :label="30">{{ $t('message.ClientProjectMonitorSystemMonitorParmater.thirtySeconds') }}</el-radio>
              <el-radio :label="0">{{ $t('message.ClientProjectMonitorSystemMonitorParmater.noUpdate') }}</el-radio>
              <el-radio :label="-1">{{ $t('message.ClientProjectMonitorSystemMonitorParmater.customInterval') }}</el-radio>
            </el-radio-group>
            
            <div v-if="trackForm.manualInterval === -1" class="custom-interval-input">
              <el-input-number 
                v-model="trackForm.customIntervalValue" 
                :min="1" 
                :max="3600"
                :step="1"
                style="width: 120px; margin-top: 8px;"
                :placeholder="$t('message.ClientProjectMonitorSystemMonitorParmater.enterInterval')"
              />
              <span class="interval-unit">{{ $t('message.ClientProjectMonitorSystemMonitorParmater.seconds') }}</span>
            </div>
          </el-form-item>

          <el-form-item 
            v-if="trackForm.updateMode === 'auto'" 
            :label="$t('message.ClientProjectMonitorSystemMonitorParmater.updateInfo')"
          >
            <div class="auto-update-info">
              <el-icon><InfoFilled /></el-icon>
              <span>{{ $t('message.ClientProjectMonitorSystemMonitorParmater.autoUpdateDesc') }}</span>
            </div>
          </el-form-item>

          <el-form-item :label="$t('message.reaTimePlot.duration')">
            <div class="duration-input">
              <el-input-number 
                v-model="trackForm.durationValue" 
                :min="1" 
                :max="480"
                style="width: 120px;"
              />
              <span class="duration-unit">{{ $t('message.ClientProjectMonitorSystemMonitorParmater.minutes') }}</span>
            </div>
            <div class="duration-tips">
              {{ $t('message.ClientProjectMonitorSystemMonitorParmater.durationTips') }}
            </div>
          </el-form-item>

          <el-form-item v-if="isTracking" :label="$t('message.ClientProjectMonitorSystemMonitorParmater.trackingStatus')">
            <div class="tracking-status">
              <el-tag type="success" size="small">
                <el-icon class="is-loading"><Loading /></el-icon>
                {{ $t('message.ClientProjectMonitorSystemMonitorParmater.trackingActive') }}
              </el-tag>
              <div class="remaining-time">
                {{ $t('message.ClientProjectMonitorSystemMonitorParmater.remainingTime') }}: 
                {{ formatRemainingTime() }}
              </div>
              <div class="current-interval" v-if="trackForm.updateMode === 'manual' && getCurrentInterval() > 0">
                {{ $t('message.ClientProjectMonitorSystemMonitorParmater.currentInterval') }}: {{ getCurrentInterval() }} {{ $t('message.ClientProjectMonitorSystemMonitorParmater.seconds') }}
              </div>
            </div>
          </el-form-item>
        </el-form>

        <div class="dialog-footer">
          <el-button 
            v-if="isTracking" 
            type="danger" 
            @click="stopTracking"
            class="stop-btn"
          >
            <el-icon><SwitchButton /></el-icon>
            {{ $t("message.ClientProjectMonitorSystemMonitorParmater.stopTrack") }}
          </el-button>      
          
          <el-button 
            type="primary" 
            @click="confirmTrack"
            :disabled="isTracking || !isFormValid"
            :loading="partDataIsRollCallLoading"
          >
            {{ isTracking ? $t("message.ClientProjectMonitorSystemMonitorParmater.tracking") : $t("message.common.confirm") }}
          </el-button>
          <el-button @click="TrackDialogVisible = false">
            {{ $t("message.common.cancel") }}
          </el-button>
        </div>
      </div>
    </el-dialog>

    <!-- Y轴范围设置对话框 -->
    <el-dialog
      v-model="showYAxisRangeDialog"
      :title="$t('message.historyRecord.setYAxisRange', { name: currentYAxisVariable?.display_name || '' })"
      width="400px"
    >
      <el-form :model="tempYAxisRange" label-width="80px">
        <el-form-item :label="$t('message.historyRecord.minValue')">
          <el-input-number
            v-model="tempYAxisRange.min"
            :precision="3"
            :step="0.1"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item :label="$t('message.historyRecord.maxValue')">
          <el-input-number
            v-model="tempYAxisRange.max"
            :precision="3"
            :step="0.1"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item v-if="yAxisManualRange[currentYAxisVariable?.name]?.enabled">
          <el-button type="danger" size="small" @click="resetYAxisRange(currentYAxisVariable?.name)">
            {{ $t('message.historyRecord.reset') }}
          </el-button>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showYAxisRangeDialog = false">
          {{ $t('message.historyRecord.cancel') }}
        </el-button>
        <el-button type="primary" @click="saveYAxisRange">
          {{ $t('message.historyRecord.confirm') }}
        </el-button>
      </template>
    </el-dialog>

    <!-- X轴范围设置对话框 -->
    <el-dialog
      v-model="showXAxisRangeDialog"
      :title="$t('message.historyRecord.xAxisRangeManagement')"      
      width="500px"
    >
      <el-form label-width="90px">
        <el-form-item :label="$t('message.historyRecord.startTimer')">
          <el-date-picker
            v-model="tempXAxisRange.min"
            type="datetime"
            :placeholder="$t('message.historyRecord.placeholderStartTimer')" 
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item :label="$t('message.historyRecord.endTimer')">
          <el-date-picker
            v-model="tempXAxisRange.max"
            type="datetime"
            :placeholder="$t('message.historyRecord.placeholderEndTimer')"  
            value-format="YYYY-MM-DD HH:mm:ss"
            style="width: 100%"
          />
        </el-form-item>
        <el-form-item v-if="xAxisManualRange.enabled">
          <el-button type="danger" size="small" @click="resetXAxisRange" plain>
            {{ $t('message.historyRecord.reset') }}
          </el-button>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showXAxisRangeDialog = false">
          {{ $t('message.historyRecord.cancel') }}
        </el-button>
        <el-button type="primary" @click="saveXAxisRange">
          {{ $t('message.historyRecord.confirm') }}
        </el-button>
      </template>
    </el-dialog>
    
    <!-- Y轴范围管理面板 -->
    <el-drawer
      v-model="showYAxisRangePanel"
      :title="$t('message.historyRecord.yAxisRangeManagement')"
      direction="rtl"
      size="400px"
    >
      <div class="yaxis-range-list">
        <div 
          v-for="variable in currentDisplayVariables" 
          :key="variable.name"
          class="yaxis-range-item"
        >
          <div class="range-item-header">
            <span class="variable-name">{{ variable.display_name }}</span>
            <el-tag 
              :type="yAxisManualRange[variable.name]?.enabled ? 'success' : 'info'"
              size="small"
            >
              {{ yAxisManualRange[variable.name]?.enabled ? $t('message.historyRecord.manual') : $t('message.historyRecord.auto') }}
            </el-tag>
          </div>
          <div class="range-item-values" v-if="yAxisManualRange[variable.name]?.enabled">
            <span>{{ $t('message.historyRecord.minValue') }}: {{ yAxisManualRange[variable.name].min.toFixed(4) }}</span>
            <span>{{ $t('message.historyRecord.maxValue') }}: {{ yAxisManualRange[variable.name].max.toFixed(4) }}</span>
          </div>
          <div class="range-item-actions">
            <el-button 
              size="small" 
              type="primary" 
              plain
              @click="openYAxisRangeSetting(variable)"
            >
              {{ $t('message.historyRecord.setYAxisRange', { name: '' }) }}
            </el-button>
            <el-button 
              v-if="yAxisManualRange[variable.name]?.enabled"
              size="small" 
              type="danger" 
              plain
              @click="resetYAxisRange(variable.name)"
            >
              {{ $t('message.historyRecord.reset') }}
            </el-button>
          </div>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  onMounted,
  onBeforeUnmount,
  onUnmounted,
  watch,
  computed,
  nextTick
} from "vue";

import * as echarts from "echarts";
import { ElMessage } from "element-plus";
import { heart } from "@/api/UserManager";
import { useI18n } from "vue-i18n";
import { useSignalR } from "@/utils/signalR";
import { getToken } from "@/utils/auth";
import { rollCall } from "@/api/DeviceManagement";
import { defineProps } from 'vue'
import { formatValue, formatValueRemove0 } from "@/api/DeviceManagement";
import { getConfig } from '@/utils/configTool';
import { ArrowDown, Refresh, InfoFilled, Loading, SwitchButton, VideoCamera, VideoPause } from '@element-plus/icons-vue';

const { t } = useI18n();

// ===== 接收传入的变量 =====
const props = defineProps({
  varDevices: {
    type: Object,
    required: true,
    default: () => ({})
  },
  projectName: {
    type: String,
    default: ''
  },
  deviceId: {
    type: [String, Number],
    default: null
  },
  // ★ 新增：父级多窗口用 v-show 控制显隐，变可见时需要 resize
  visible: {
    type: Boolean,
    default: true
  }
});

// ===== 变量定义 =====
const allVariables = computed(() => {
  const variables = [];
  for (const [deviceCode, varObjects] of Object.entries(props.varDevices)) {
    varObjects.forEach(varObj => {
      variables.push({
        name: varObj.name,
        display_name: varObj.title,
        deviceCode: deviceCode,
        mult: varObj.mult,
        var_decimal: varObj.var_decimal,
        companyId: varObj.companyId
      });
    });
  }
  return variables;
});

const variableMap = computed(() => {
  const map = new Map();
  allVariables.value.forEach(variable => {
    map.set(variable.name, variable);
  });
  return map;
});

const getMultAndDecimal = (varName) => {
  if (!varName || typeof varName !== 'string') {
    return { mult: 1, var_decimal: 0 };
  }
  const variable = variableMap.value.get(varName);
  const mult = Number(variable?.mult);
  const var_decimal = Number(variable?.var_decimal);
  return {
    mult: isFinite(mult) ? mult : 1,
    var_decimal: isFinite(var_decimal) ? var_decimal : 0
  };
};

const selectedVarNames = ref([]);
const currentDisplayVariables = ref([]);
const monitoringDuration = ref(1);
const MAX_CACHE_HOURS = ref(0);
const chartRef = ref(null);
let chartInstance = null;
let chartResizeObserver = null;        // ★ 新增：图表容器尺寸观察器
let resizeRafId = 0;                   // ★ 新增：rAF 节流用
const cachedData = ref([]);

// ===== 实时数据接收开关 =====
const isDataReceiving = ref(true);

// ===== Y轴范围控制相关变量 =====
const yAxisManualRange = ref({});
const showYAxisRangeDialog = ref(false);
const showYAxisRangePanel = ref(false);
const currentYAxisVariable = ref(null);
const tempYAxisRange = ref({ min: '', max: '' });
const yAxisRanges = ref({});
const legendSelectedState = ref({});

// ===== X轴范围控制相关变量（参照历史数据页面） =====
const xAxisManualRange = ref({ min: null, max: null, enabled: false });
const showXAxisRangeDialog = ref(false);
const tempXAxisRange = ref({ min: '', max: '' });

// ===== 追踪相关变量 =====
const isTracking = ref(false);
const TrackDialogVisible = ref(false);
const partDataIsRollCallLoading = ref(false);
const remainingTime = ref(0);
let trackTimer = null;
let remainingTimeTimer = null;
const trackingStartTime = ref(null);

const trackForm = ref({
  updateMode: 'manual',
  manualInterval: 5,
  customIntervalValue: 60,
  durationValue: 10,
});

// ===== SignalR 相关 =====
const url = ref('');
const {
  isConnected,
  isReconnecting,
  reconnectAttempts,
  reconnectDelay,
  start,
  stop,
  invoke,
  on,
  off,
  onConnected,
} = useSignalR(url, {
  accessTokenFactory: () => getToken() || "",
  automaticReconnect: true,
});

const reconnectMessage = computed(() => {
  return t("message.reaTimePlot.reconnecting", {
    attempts: reconnectAttempts.value,
    seconds: Math.round(reconnectDelay.value / 1000),
  });
});

// ===== 分页配置 =====
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0,
});

// ===== 定时器相关 =====
let timer = null;
const refreshInterval = 30 * 1000;
let heartTimer = null;
const refreshHeartInterval = 10 * 60 * 1000;

// ===== 表单验证 =====
const isFormValid = computed(() => {
  if (trackForm.value.updateMode === 'manual') {
    if (trackForm.value.manualInterval === -1) {
      return trackForm.value.customIntervalValue > 0 && trackForm.value.customIntervalValue <= 3600;
    }
  }
  return true;
});

const getCurrentInterval = () => {
  if (trackForm.value.updateMode === 'auto') return 0;
  if (trackForm.value.updateMode === 'manual') {
    if (trackForm.value.manualInterval === -1) return trackForm.value.customIntervalValue;
    return trackForm.value.manualInterval;
  }
  return 0;
};

const formatRemainingTime = () => {
  if (!isTracking.value || remainingTime.value <= 0) return '00:00';
  const minutes = Math.floor(remainingTime.value / 60);
  const seconds = remainingTime.value % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const updateRemainingTime = () => {
  if (isTracking.value && trackingStartTime.value) {
    const elapsed = Math.floor((Date.now() - trackingStartTime.value) / 1000);
    const totalDuration = trackForm.value.durationValue * 60;
    remainingTime.value = Math.max(0, totalDuration - elapsed);
    if (remainingTime.value <= 0) {
      stopTracking();
    }
  }
};

// ===== 加载配置 =====
async function loadConfig() {
  try {
    const config = await getConfig();
    url.value = config.signalRUrl;
  } catch (error) {
    console.error('加载配置文件失败：', error);
  }
}

// ===== 更新显示变量 =====
const updateDisplayVariables = () => {
  if (selectedVarNames.value.length === 0) {
    currentDisplayVariables.value = allVariables.value;
  } else {
    currentDisplayVariables.value = allVariables.value.filter(variable => 
      selectedVarNames.value.includes(variable.name)
    );
  }
};

// ===== 工具函数 =====
const getVariableNames = () => {
  if (selectedVarNames.value.length === 0) {
    return allVariables.value.map(v => v.name);
  }
  return selectedVarNames.value;
};

// ===== 格式化本地时间 =====
const formatLocalDateTime = (date) => {
  if (!date) return '';
  if (typeof date === 'string') {
    const parsed = new Date(date);
    if (!isNaN(parsed.getTime())) {
      date = parsed;
    } else {
      return date;
    }
  }
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

// ===== 颜色工具 =====
const hexToRgb = (hex) => {
  hex = hex.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
};

const getColorByIndex = (index) => {
  const colors = [
    '#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de',
    '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc', '#ff9f7f',
    '#1e90ff', '#32cd32', '#ff4500', '#6a5acd', '#ff69b4',
    '#00ced1', '#ffa500', '#8a2be2', '#5f9ea0', '#d2691e',
    '#6495ed', '#dc143c', '#00ff7f', '#ff1493', '#00bfff',
    '#696969', '#ff8c00', '#7cfc00', '#da70d6', '#cd5c5c',
    '#4682b4', '#9acd32', '#ff6347', '#8470ff', '#ff6eb4',
    '#00eeee', '#ff7f50', '#9370db', '#66cdaa', '#f08080'
  ];
  return colors[index % colors.length];
};

// ===== Y轴范围计算 =====
const calculateYAxisRange = (data, variableName) => {
  const manualRange = yAxisManualRange.value[variableName];
  if (manualRange && manualRange.enabled) {
    return { min: manualRange.min, max: manualRange.max };
  }
  
  const values = data
    .map(item => parseFloat(item[variableName]))
    .filter(value => !isNaN(value));
  
  if (values.length === 0) {
    return { min: 0, max: 1 };
  }
  
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const padding = range * 0.1 || 0.1;
  return { min: min - padding, max: max + padding };
};

// ===== 创建动态Y轴配置 =====
const createDynamicYAxisConfig = (variables, processedData) => {
  const yAxisConfig = [];
  
  for (let i = 0; i < variables.length; i++) {
    const variable = variables[i];
    const isLeft = i % 2 === 0;
    const axisIndex = Math.floor(i / 2);
    const offset = axisIndex * 60;
    
    const range = calculateYAxisRange(processedData, variable.name);
    yAxisRanges.value[variable.name] = range;
    
    const verticalName = variable.display_name.split('').join('\n');
    
    yAxisConfig.push({
      type: 'value',
      name: verticalName,
      position: isLeft ? 'left' : 'right',
      offset: offset,
      axisLine: {
        show: true,
        lineStyle: {
          color: getColorByIndex(i),
          width: 1.5
        },
        onZero: false
      },
      axisTick: {
        show: true,
        lineStyle: {
          color: getColorByIndex(i)
        }
      },
      axisLabel: {
        formatter: function(value) {
          if (Math.abs(value) < 0.001 && value !== 0) {
            return value.toExponential(3);
          } else if (Math.abs(value) < 1) {
            return value.toFixed(4);
          } else if (Math.abs(value) < 10) {
            return value.toFixed(3);
          } else if (Math.abs(value) < 100) {
            return value.toFixed(2);
          } else if (Math.abs(value) < 1000) {
            return value.toFixed(1);
          } else if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M';
          } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + 'k';
          }
          return Number(value.toFixed(0)).toString();
        },
        color: getColorByIndex(i),
        fontSize: 11,
        show: true,
        margin: isLeft ? 15 : 8,
        padding: [0, isLeft ? -10 : 0, 0, isLeft ? 0 : 0],
        align: isLeft ? 'right' : 'left',
        verticalAlign: 'middle'
      },
      nameTextStyle: {
        color: getColorByIndex(i),
        fontSize: 10,
        fontWeight: 'bold',
        align: 'center',
        lineHeight: 16,
        verticalAlign: 'top',
        padding: isLeft ? [50, 0, 0, 15] : [0, 15, 0, 0]
      },
      nameLocation: 'end',
      nameGap: isLeft ? 35 : -30,
      splitLine: { show: false },
      min: range.min,
      max: range.max,
      scale: false,
      boundaryGap: ['10%', '10%'],
      z: 100 + i
    });
  }
  
  return yAxisConfig;
};

// ===== 数据处理 =====
const processDeviceData = (jsonStr) => {
  if (!isDataReceiving.value) {
    return cachedData.value;
  }
  
  try {
    const receivedData = JSON.parse(jsonStr);
    
    // 时间解析
    let timeValue = receivedData.time || 
                    receivedData.Time || 
                    receivedData.timestamp || 
                    receivedData.Timestamp ||
                    receivedData.recTime ||
                    receivedData.rec_time ||
                    receivedData.date ||
                    receivedData.Date;
    
    let formattedTime;
    let receivedTime;

    if (timeValue) {
      if (typeof timeValue === 'string') {
        receivedTime = new Date(timeValue);
        if (isNaN(receivedTime.getTime())) {
          const fixedTime = timeValue.replace('T', ' ');
          receivedTime = new Date(fixedTime);
        }
      } else if (typeof timeValue === 'number') {
        if (timeValue > 100000000000) {
          receivedTime = new Date(timeValue);
        } else if (timeValue > 1000000000) {
          receivedTime = new Date(timeValue * 1000);
        } else {
          receivedTime = new Date(timeValue);
        }
      } else if (timeValue instanceof Date) {
        receivedTime = timeValue;
      } else {
        receivedTime = new Date(timeValue);
      }
    } else {
      receivedTime = new Date();
    }
    
    if (isNaN(receivedTime.getTime()) || receivedTime.getFullYear() === 1970) {
      console.warn('时间解析失败，使用当前时间。原始值:', timeValue);
      receivedTime = new Date();
    }
    
    formattedTime = formatLocalDateTime(receivedTime);

    const existingDataIndex = cachedData.value.findIndex(
      item => item.timestamp === formattedTime && item.DeviceCode === receivedData.DeviceCode
    );

    let newDataItem;

    if (existingDataIndex !== -1) {
      newDataItem = cachedData.value[existingDataIndex];
    } else {
      newDataItem = {
        timestamp: formattedTime,
        rawTime: receivedTime,
        DeviceCode: receivedData.DeviceCode,
        imei: receivedData.imei,
        Type: receivedData.Type,
        Priority: receivedData.Priority,
      };
    }

    if (receivedData.Vars && Array.isArray(receivedData.Vars)) {
      receivedData.Vars.forEach((variable) => {
        if (variable && variable.Name && variable.Error == 0) {
          const { mult, var_decimal } = getMultAndDecimal(variable.Name);
          newDataItem[variable.Name] = formatValue(variable.Value, mult, var_decimal);
          newDataItem[`${variable.Name}_Error`] = variable.Error ?? 0;
        }
      });
    }

    if (existingDataIndex === -1) {
      cachedData.value.push(newDataItem);
    } else {
      cachedData.value[existingDataIndex] = newDataItem;
    }

    pagination.total = cachedData.value.length;

    const now = new Date();
    MAX_CACHE_HOURS.value = monitoringDuration.value * 60 * 60 * 1000;
    const cacheTimeAgo = new Date(now.getTime() - MAX_CACHE_HOURS.value);
    cachedData.value = cachedData.value.filter(
      (item) => item.rawTime > cacheTimeAgo
    );

    cachedData.value.sort((a, b) => b.rawTime - a.rawTime);
    return cachedData.value;
  } catch (error) {
    console.error("数据处理错误:", error);
    ElMessage.error(t("message.reaTimePlot.dataProcessError"));
    return [];
  }
};

// ★ resize 节流：ResizeObserver 触发非常频繁（拖拽时每帧），用 rAF 合并
const scheduleResize = () => {
  if (resizeRafId) return;
  resizeRafId = requestAnimationFrame(() => {
    resizeRafId = 0;
    if (chartInstance && chartRef.value && chartRef.value.offsetWidth > 0 && chartRef.value.offsetHeight > 0) {
      chartInstance.resize({ duration: 0 });
    }
  });
};

// ===== 图表初始化 =====t("message.reaTimePlot.deviceVariableMonitor")
const initChart = () => {
  if (!chartRef.value) return;
  
  try {
    chartInstance = echarts.init(chartRef.value);
    
    const option = {
      title: {
        text: '',
      },
      tooltip: {
        trigger: "axis",
      },
      legend: {
        data: [],
      },
      grid: {
        left: "3%",
        right: "40px",
        bottom: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "time",
        boundaryGap: false,
        axisLabel: {
          rotate: 0,
          fontSize: 15,
          margin: 8,
        }
      },
      yAxis: {
        type: "value",
      },
      series: [],
      dataZoom: [
        { 
          type: "slider",
          xAxisIndex: [0],
          start: 0, 
          end: 100,
          bottom: 10,
          height: 20,
          show: true
        },
        { 
          type: "inside", 
          yAxisIndex: [0],
          start: 0, 
          end: 100,
          zoomLock: false
        },
        { 
          type: "slider",
          yAxisIndex: [0],
          start: 0, 
          end: 100,
          right: 20,
          width: 12,
          show: true,
          zoomLock: false,
          brushSelect: false
        }
      ]
    };

    chartInstance.setOption(option);

    // ★ 监听图表容器尺寸变化：拖拽分隔条、关闭其它 panel、窗口 resize、
    //   display:none → block 都会触发，自动 resize
    if (window.ResizeObserver && chartRef.value) {
      chartResizeObserver = new ResizeObserver(scheduleResize);
      chartResizeObserver.observe(chartRef.value);
    }
  } catch (error) {
    console.error('Chart initialization failed:', error);
  }
};

// ===== 聚合函数 =====
const aggregateData = (data, maxPoints = 3000) => {
  if (data.length <= maxPoints) return data;

  console.log(`数据量 ${data.length} 超过 ${maxPoints}，进行聚合处理`);
  
  const aggregatedData = [];
  const interval = Math.ceil(data.length / maxPoints);
  
  for (let i = 0; i < data.length; i += interval) {
    const chunk = data.slice(i, i + interval);
    if (chunk.length === 0) continue;
    
    const middleIndex = Math.floor(chunk.length / 2);
    const aggregatedItem = {
      ...chunk[middleIndex],
      timestamp: chunk[middleIndex].timestamp,
      rawTime: chunk[middleIndex].rawTime,
      // ✅ 保存时间戳数值用于 X轴排序
      timestampMs: new Date(chunk[middleIndex].timestamp).getTime()
    };
    
    currentDisplayVariables.value.forEach(variable => {
      const values = chunk
        .map(item => parseFloat(item[variable.name]))
        .filter(value => !isNaN(value));
      
      if (values.length > 0) {
        const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
        aggregatedItem[variable.name] = Number(avg.toFixed(4));
      } else {
        aggregatedItem[variable.name] = null;
      }
    });
    
    aggregatedData.push(aggregatedItem);
  }
  
  console.log(`聚合后数据量: ${aggregatedData.length}`);
  return aggregatedData;
};

// ===== 应用X轴范围到图表（参照历史数据页面） =====
const applyXAxisRangeToChart = () => {
  if (!chartInstance) return;
  
  if (xAxisManualRange.value.enabled && xAxisManualRange.value.min && xAxisManualRange.value.max) {
    const minTimestamp = new Date(xAxisManualRange.value.min).getTime();
    const maxTimestamp = new Date(xAxisManualRange.value.max).getTime();
    chartInstance.setOption({
      xAxis: {
        min: minTimestamp,
        max: maxTimestamp
      }
    });
  } else {
    chartInstance.setOption({
      xAxis: {
        min: null,
        max: null
      }
    });
  }
};

// ===== 更新图表 =====
const updateChart = (data) => {
  if (!chartInstance || !currentDisplayVariables.value.length) return;

  try {
    if (currentDisplayVariables.value.length > 20) {
      console.warn('变量数量较多，可能会影响图表性能');
    }

    // ✅ 按时间戳排序（使用数值比较）
    const sortedData = [...data].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    const processedData = aggregateData(sortedData, 3000);
    const displayVars = currentDisplayVariables.value;
    const yAxisConfig = createDynamicYAxisConfig(displayVars, processedData);
    
  
    
    const initialLegendSelected = {};
    displayVars.forEach(variable => {
      initialLegendSelected[variable.display_name] = 
        legendSelectedState.value[variable.display_name] !== undefined 
          ? legendSelectedState.value[variable.display_name] 
          : true;
    });
    
    const updatedLegendState = { ...legendSelectedState.value };
    displayVars.forEach(variable => {
      if (updatedLegendState[variable.display_name] === undefined) {
        updatedLegendState[variable.display_name] = true;
      }
    });
    legendSelectedState.value = updatedLegendState;

    const seriesData = displayVars.map((variable, index) => {
      const isSelected = legendSelectedState.value[variable.display_name] !== false;
      
      return {
        name: variable.display_name,
        type: "line",
        yAxisIndex: index,
        symbol: isSelected ? "circle" : "none",
        symbolSize: isSelected ? 3 : 0,
        smooth: true,
        connectNulls: true,
        lineStyle: {
          width: 1.2,
          opacity: isSelected ? 1 : 0.2
        },
        itemStyle: {
          borderWidth: 1,
          borderColor: "#fff",
          opacity: isSelected ? 1 : 0.2
        },
        // ✅ 数据格式：[timestamp, value]
        data: processedData.map((item) => {
          const value = parseFloat(item[variable.name]);
          const timestamp = new Date(item.timestamp).getTime();
          return isNaN(value) ? null : [timestamp, value];
        }),
        emphasis: {
          disabled: false,
          lineStyle: {
            width: 2,
            opacity: 1
          }
        }
      };
    });

    const yAxisDataZoom = [];
    const yAxisSliderDataZoom = [];
    const yAxisToSliderDistance = 20;
    const sliderSpacing = 14;
    
    displayVars.forEach((_, index) => {
      yAxisDataZoom.push({
        type: "inside", 
        yAxisIndex: index,
        start: 0, 
        end: 100,
        zoomLock: false
      });
      
      const sliderRight = yAxisToSliderDistance + index * sliderSpacing;
      const yAxisColor = getColorByIndex(index);

      yAxisSliderDataZoom.push({
        type: "slider",
        yAxisIndex: index,
        start: 0, 
        end: 100,
        right: sliderRight,
        width: 10,
        show: true,
        zoomLock: false,
        brushSelect: false,
        fillerColor: `rgba(${hexToRgb(yAxisColor)}, 0.2)`,
        borderColor: yAxisColor,
        handleSize: 6,
        handleStyle: {
          color: yAxisColor
        },
        backgroundColor: 'rgba(240, 242, 245, 0.8)',
        textStyle: {
          color: '#606266',
          fontSize: 15
        },
        dataBackground: {
          lineStyle: {
            color: yAxisColor,
            opacity: 0.3
          },
          areaStyle: {
            color: yAxisColor
          }
        }
      });
    });

    const totalSlidersWidth = displayVars.length * 10 + (displayVars.length - 1) * 6;
    const gridRight = yAxisToSliderDistance + totalSlidersWidth + 60;
    const leftYAxisCount = displayVars.filter((_, i) => i % 2 === 0).length;
    const gridLeft = leftYAxisCount === 0 ? '3%' : `${Math.max(24, leftYAxisCount * 15)}px`;
    const legendTop = displayVars.length > 8 ? '40px' : '0px';
    const gridTop = displayVars.length > 8 ? '80px' : '50px';

    // ===== X轴配置（参照历史数据页面） =====
    let xAxisMin = 'dataMin', xAxisMax = 'dataMax';
    if (xAxisManualRange.value.enabled && xAxisManualRange.value.min && xAxisManualRange.value.max) {
      try {
        const minTs = new Date(xAxisManualRange.value.min).getTime();
        const maxTs = new Date(xAxisManualRange.value.max).getTime();
        if (!isNaN(minTs) && !isNaN(maxTs) && minTs < maxTs) {
          xAxisMin = minTs;
          xAxisMax = maxTs;
        }
      } catch (e) {
        console.warn('X轴范围解析失败，使用默认范围');
      }
    }

    const option = {
      legend: {
        data: displayVars.map(v => v.display_name),
        selected: initialLegendSelected,
        top: legendTop,
        type: 'scroll',
        pageButtonItemGap: 3,
        pageButtonPosition: 'end',
        pageFormatter: '{current}/{total}',
        pageTextStyle: {
          fontSize: 15
        },
        textStyle: {
          fontSize: 15
        },
        itemHeight: 10,
        itemWidth: 20,
        itemGap: 3,
        selector: false,
        inactiveColor: '#ccc'
      },
      xAxis: {
        type: "time",
        boundaryGap: false,
        min: xAxisMin,
        max: xAxisMax,
        scale: true,
        axisLabel: {
          rotate: 0,
          formatter: (value) => {
            const date = new Date(value);
            if (isNaN(date.getTime())) return '';
            const h = String(date.getHours()).padStart(2, '0');
            const m = String(date.getMinutes()).padStart(2, '0');
            if (h === '00' && m === '00') return `${date.getMonth() + 1}/${date.getDate()}`;
            if (m === '00') return `${h}:00`;
            return '';
          },
          fontSize: 12,
          margin: 8,
          interval: 'auto'
        },
        axisLine: {
          lineStyle: {
            color: '#dcdfe6'
          }
        },
        axisTick: {
          lineStyle: {
            color: '#dcdfe6'
          }
        }
      },
      yAxis: yAxisConfig,
      series: seriesData,
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          label: {
            backgroundColor: "#6a7985",
            formatter: (params) => {
              if (params.axisDimension === 'x') {
                const date = new Date(params.value);
                if (!isNaN(date.getTime())) {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  const hours = String(date.getHours()).padStart(2, '0');
                  const minutes = String(date.getMinutes()).padStart(2, '0');
                  const seconds = String(date.getSeconds()).padStart(2, '0');
                  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
                }
                return params.value;
              }
              if (params.axisDimension === 'y') {
                if (typeof params.value === 'number') {
                  return parseFloat(params.value.toFixed(2)).toString();
                }
                return params.value;
              }
              return params.value;
            }
          },
        },
        confine: true,
        extraCssText: 'max-width: 300px; word-break: break-all;',
        textStyle: {
          fontSize: 15
        },
        formatter: (params) => {
          let result = '';
          if (params && params.length > 0 && params[0]) {
            const date = new Date(params[0].axisValue);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              const seconds = String(date.getSeconds()).padStart(2, '0');
              
              const isAggregated = cachedData.value.length > 3000;
              if (isAggregated) {
                result += `<div style="margin-bottom: 4px; color: #909399; font-size: 12px;">📊 数据已聚合 (${cachedData.value.length} → ${processedData.length} 个点)</div>`;
              }
              
              result += `<div style="margin-bottom: 8px; font-weight: bold;">${year}-${month}-${day} ${hours}:${minutes}:${seconds}</div>`;
            } else {
              result += `<div style="margin-bottom: 8px; font-weight: bold;">${params[0].axisValue}</div>`;
            }
          }
          
          if (params) {
            params.forEach((item) => {
              const val = item.value !== null && item.value !== undefined 
                ? typeof item.value[1] === 'number' 
                  ? formatValueRemove0(item.value[1])
                  : item.value
                : '--';
              result += `
                <div style="display: flex; align-items: center; margin: 4px 0;">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${item.color}; margin-right: 8px;"></span>
                  <span style="flex: 1;">${item.seriesName}:</span>
                  <span style="font-weight: bold; margin-left: 8px;">${val}</span>
                </div>
              `;
            });
          }
          return result;
        }
      },
      grid: {
        left: gridLeft,
        right: `${gridRight}px`,
        bottom: "4%",
        top: gridTop,
        containLabel: true,
      },
      dataZoom: [
        { 
          type: "slider",
          xAxisIndex: [0],
          start: 0, 
          end: 100,
          bottom: 5,
          height: 14,
          show: true,
          handleSize: 8,
          handleStyle: {
            color: '#5470c6'
          },
          backgroundColor: 'rgba(240, 242, 245, 0.8)',
          borderColor: '#dcdfe6',
          fillerColor: 'rgba(84, 112, 198, 0.3)',
          dataBackground: {
            lineStyle: {
              color: '#909399',
              opacity: 0.3
            },
            areaStyle: {
              color: '#909399',
              opacity: 0.1
            }
          },
          selectedDataBackground: {
            lineStyle: {
              color: '#5470c6',
              opacity: 0.6
            },
            areaStyle: {
              color: '#5470c6',
              opacity: 0.2
            }
          }
        },
        ...yAxisDataZoom,
        ...yAxisSliderDataZoom
      ],
      animation: false,
      animationDuration: 0
    };

    chartInstance.off('legendselectchanged');
    chartInstance.setOption(option, { notMerge: true });

    chartInstance.on('legendselectchanged', function(params) {
      const selected = params.selected;
      const changedSeries = params.name;
      
      if (selected[changedSeries] !== undefined) {
        legendSelectedState.value[changedSeries] = selected[changedSeries];
      }
      
      const currentOption = chartInstance.getOption();
      const series = currentOption.series;
      const seriesIndex = series.findIndex(s => s.name === changedSeries);
      
      if (seriesIndex !== -1) {
        const isSelected = selected[changedSeries];
        const updatedSeries = series.map((seriesItem, index) => {
          if (index === seriesIndex) {
            return {
              ...seriesItem,
              lineStyle: {
                ...seriesItem.lineStyle,
                opacity: isSelected ? 1 : 0.2
              },
              itemStyle: {
                ...seriesItem.itemStyle,
                opacity: isSelected ? 1 : 0.2
              },
              symbol: isSelected ? 'circle' : 'none',
              symbolSize: isSelected ? 3 : 0
            };
          }
          return seriesItem;
        });
        
        chartInstance.setOption({
          series: updatedSeries
        }, {
          notMerge: false,
          replaceMerge: 'series',
          lazyUpdate: false
        });
      }
    });
    
    setTimeout(() => {
      if (!chartInstance || chartInstance.isDisposed()) return;
      if (chartRef.value && chartRef.value.offsetWidth > 0) {
        chartInstance.resize({ duration: 0 });
      }
    }, 100);

  } catch (error) {
    console.error('Chart update failed:', error);
  }
};

// ===== 实时数据开关控制 =====
const toggleDataReceiving = () => {
  isDataReceiving.value = !isDataReceiving.value;
  
  if (isDataReceiving.value) {
    // ✅ 恢复接收时，清除X轴范围设置，恢复全量显示
    xAxisManualRange.value = { min: null, max: null, enabled: false };
    tempXAxisRange.value = { min: '', max: '' };
    
    // 应用清除X轴范围到图表
    applyXAxisRangeToChart();
    
    ElMessage.success(t('message.reaTimePlot.dataReceivingResumed') );
    if (selectedVarNames.value.length > 0 && isConnected.value) {
      sendMessage();
    }
  } else {
    ElMessage.info(t('message.reaTimePlot.dataReceivingPaused'));
  }
};

// ===== Y轴范围控制方法 =====
const openYAxisRangeSetting = (variable) => {
  currentYAxisVariable.value = variable;
  const currentRange = yAxisManualRange.value[variable.name];
  if (currentRange && currentRange.enabled) {
    tempYAxisRange.value = {
      min: currentRange.min.toFixed(4),
      max: currentRange.max.toFixed(4)
    };
  } else {
    const currentData = cachedData.value;
    const range = calculateYAxisRange(currentData, variable.name);
    tempYAxisRange.value = {
      min: range.min.toFixed(4),
      max: range.max.toFixed(4)
    };
  }
  showYAxisRangeDialog.value = true;
};

const saveYAxisRange = () => {
  if (!currentYAxisVariable.value) return;
  
  const min = parseFloat(tempYAxisRange.value.min);
  const max = parseFloat(tempYAxisRange.value.max);
  
  if (isNaN(min) || isNaN(max)) {
    ElMessage.warning(t('message.historyRecord.invalidRange'));
    return;
  }
  
  if (min >= max) {
    ElMessage.warning(t('message.historyRecord.minGreaterThanMax'));
    return;
  }
  
  yAxisManualRange.value[currentYAxisVariable.value.name] = {
    min: min,
    max: max,
    enabled: true
  };
  
  showYAxisRangeDialog.value = false;
  updateChart(cachedData.value);
  ElMessage.success(t('message.historyRecord.rangeSetSuccess'));
};

const resetYAxisRange = (variableName) => {
  if (variableName) {
    delete yAxisManualRange.value[variableName];
  } else {
    yAxisManualRange.value = {};
  }
  updateChart(cachedData.value);
  ElMessage.success(t('message.historyRecord.rangeResetSuccess'));
};

const clearAllYAxisRanges = () => {
  yAxisManualRange.value = {};
  updateChart(cachedData.value);
  ElMessage.success(t('message.historyRecord.allRangesCleared'));
};

// ===== X轴范围控制方法（参照历史数据页面） =====
const openXAxisRangeSetting = () => {
  if (isDataReceiving.value) {
    ElMessage.warning(t('message.historyRecord.stopDataFirst') || '请先停止接收实时数据再设置X轴范围');
    return;
  }
  
  const currentData = cachedData.value;
  
  if (!currentData || currentData.length === 0) {
    ElMessage.warning(t('message.historyRecord.noDataForXAxis') || '暂无数据，请先查询');
    return;
  }
  
  if (xAxisManualRange.value.enabled) {
    tempXAxisRange.value = {
      min: xAxisManualRange.value.min,
      max: xAxisManualRange.value.max
    };
  } else {
    const sortedData = [...currentData].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    if (sortedData.length > 0) {
      tempXAxisRange.value = {
        min: sortedData[0]?.timestamp || '',
        max: sortedData[sortedData.length - 1]?.timestamp || ''
      };
    } else {
      tempXAxisRange.value = { min: '', max: '' };
    }
  }
  
  showXAxisRangeDialog.value = true;
};

const saveXAxisRange = () => {
  const min = tempXAxisRange.value.min;
  const max = tempXAxisRange.value.max;
  
  if (!min || !max) {
    ElMessage.warning(t('message.historyRecord.selectValidTimeRange'));
    return;
  }
  
  const minTime = new Date(min).getTime();
  const maxTime = new Date(max).getTime();
  
  if (minTime >= maxTime) {
    ElMessage.warning(t('message.historyRecord.startTimeLessThanEndTime'));
    return;
  }
  
  xAxisManualRange.value = {
    min: min,
    max: max,
    enabled: true
  };
  
  showXAxisRangeDialog.value = false;
  
  applyXAxisRangeToChart();
  ElMessage.success(t('message.historyRecord.xAxisRangeSetSuccess', { min, max }));
};

const resetXAxisRange = () => {
  xAxisManualRange.value = { min: null, max: null, enabled: false };
  showXAxisRangeDialog.value = false;
  applyXAxisRangeToChart();
  ElMessage.success(t('message.historyRecord.rangeXResetSuccess'));
};

const clearXAxisRange = () => {
  xAxisManualRange.value = { min: null, max: null, enabled: false };
  tempXAxisRange.value = { min: '', max: '' };
  if (showXAxisRangeDialog.value) {
    showXAxisRangeDialog.value = false;
  }
  applyXAxisRangeToChart();
  ElMessage.success(t('message.historyRecord.XRangesCleared'));
};

// ===== 轴命令处理 =====
const handleAxisCommand = (command) => {
  if (command === 'showYAxisSettings') {
    showYAxisRangePanel.value = true;
  } else if (command === 'showXAxisControl') {
    openXAxisRangeSetting();
  } else if (command === 'clearXAxisRange') {
    clearXAxisRange();
  } else if (command === 'clearAllYAxis') {
    clearAllYAxisRanges();
  }
};

// ===== 分组和分块辅助函数 =====
const groupVariablesByDevice = () => {
  const deviceGroups = {};
  selectedVarNames.value.forEach(varName => {
    let foundDevice = null;
    for (const [deviceCode, variables] of Object.entries(filteredVarDevices.value)) {
      if (variables.some(v => v.name === varName)) {
        foundDevice = deviceCode;
        break;
      }
    }
    if (foundDevice) {
      if (!deviceGroups[foundDevice]) {
        deviceGroups[foundDevice] = [];
      }
      deviceGroups[foundDevice].push(varName);
    }
  });
  return deviceGroups;
};

const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

// ===== SignalR 订阅相关 =====
const sendMessage = async () => {
  if (!isDataReceiving.value) {
    return;
  }
  
  if (!isConnected.value || selectedVarNames.value.length === 0) return;

  try {
    const token = getToken();
    if (!token) return;

    const deviceCodes = Object.keys(filteredVarDevices.value);
    if (deviceCodes.length === 0) return;

    const deviceCode = deviceCodes[0];

    const params = {
      token: token,
      deviceCode: deviceCode,
      imei: undefined,
      Dir: "d",
      type: 1,
      timer: new Date().toISOString(),
      priority: 1,
      r_data: getVariableNames().map(name => ({ name })),
    };

    const jsonString = JSON.stringify(params);
    await invoke("RequestData", jsonString);
  } catch (err) {
    console.error("Failed to send message:", err);
  }
};

const stopSubscription = async () => {
  if (!isConnected.value) return;
  
  try {
    const token = getToken();
    if (!token) return;

    const deviceCodes = Object.keys(filteredVarDevices.value);
    if (deviceCodes.length === 0) return;

    const deviceCode = deviceCodes[0];

    const params = {
      token: token,
      deviceCode: deviceCode,
      imei: undefined,
      Dir: "d",
      type: 1,
      priority: 1,
      timer: new Date().toISOString(),
      r_data: [],
    };

    const jsonString = JSON.stringify(params);
    await invoke("ReleaseData", jsonString);
    console.log("订阅已停止");
  } catch (error) {
    console.error("停止订阅失败:", error);
  }
};

const handleNotification = async (message) => {
  if (!isDataReceiving.value) {
    return;
  }
  
  try {
    if (isTracking.value === true && trackForm.value.updateMode === 'manual' && trackForm.value.manualInterval === 0) {
      console.log('停止实时数据更新');
      return;
    }
    const processedData = processDeviceData(message);
    updateChart(processedData);
  } catch (error) {
    console.error("处理数据错误:", error);
    ElMessage.error(t("message.reaTimePlot.dataProcessError"));
  }
};

// ===== 点名功能 =====
const rollCallClick = async () => {
  if (selectedVarNames.value.length === 0) {
    ElMessage.error(t("message.reaTimePlot.selectVariable"));
    return;
  }

  if (!isConnected.value) {
    ElMessage.error(t("message.reaTimePlot.connectError"));
    return;
  }

  try {
    const token = getToken();
    if (!token) return;

    const deviceVariableGroups = groupVariablesByDevice();
    if (Object.keys(deviceVariableGroups).length === 0) {
      ElMessage.warning(t("message.reaTimePlot.noValidVariables"));
      return;
    }

    const rollCallPromises = [];
    
    for (const [deviceCode, variableNames] of Object.entries(deviceVariableGroups)) {
      if (variableNames.length === 0) continue;
      
      const variableChunks = chunkArray(variableNames, 5);
      
      for (let i = 0; i < variableChunks.length; i++) {
        const chunk = variableChunks[i];
        
        const requestData = {
          token: token,
          deviceCode: deviceCode,
          imei: undefined,
          type: 4,
          dir: "Down",
          timer: new Date().toISOString(),
          priority: 1,
          r_data: chunk.map(name => ({ name })),
          w_data: [],
        };

        rollCallPromises.push(
          rollCall([requestData])
            .then(() => {
              console.log(`设备 ${deviceCode} 第${i + 1}/${variableChunks.length}包点名成功，变量数量: ${chunk.length}`);
            })
            .catch(error => {
              console.error(`设备 ${deviceCode} 第${i + 1}包点名失败:`, error);
              throw error;
            })
        );
        
        if (i < variableChunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    }

    await Promise.all(rollCallPromises);
    
  } catch (error) {
    console.error('点名操作失败:', error);
    ElMessage.error(
      error.response?.data?.message || 
      error.message || 
      t("message.reaTimePlot.dataProcessError")
    );
  }
};

// ===== 追踪功能 =====
const handleTrack = () => {
  if (selectedVarNames.value.length === 0) {
    ElMessage.error(t("message.reaTimePlot.selectVariable"));
    return;
  }
  TrackDialogVisible.value = true;
};

const handleStopTrack = () => {
  stopTracking();
};

const stopTracking = () => {
  if (trackTimer) {
    clearInterval(trackTimer);
    trackTimer = null;
  }
  if (remainingTimeTimer) {
    clearInterval(remainingTimeTimer);
    remainingTimeTimer = null;
  }
  partDataIsRollCallLoading.value = false;
  isTracking.value = false;
  trackingStartTime.value = null;
  remainingTime.value = 0;
};

const confirmTrack = async () => {
  if (trackTimer) {
    clearInterval(trackTimer);
    trackTimer = null;
  }

  try {
    if (!isConnected.value) {
      ElMessage.error(t("message.ClientProjectMonitorSystemMonitorParmater.connectError"));
      return;
    }
    
    if (!isFormValid.value) {
      ElMessage.warning(t("message.ClientProjectMonitorSystemMonitorParmater.invalidInterval"));
      return;
    }
    
    const currentVariables = getVariableNames();
    if (currentVariables.length === 0) {
      ElMessage.warning(t("message.ClientProjectMonitorSystemMonitorParmater.noVariables"));
      return;
    }

    partDataIsRollCallLoading.value = true;
    isTracking.value = true;
    trackingStartTime.value = Date.now();
    remainingTime.value = trackForm.value.durationValue * 60;

    remainingTimeTimer = setInterval(updateRemainingTime, 1000);
    await rollCallClick();
    
    if (trackForm.value.updateMode === 'manual') {
      const interval = getCurrentInterval();
      if (interval > 0) {
        trackTimer = setInterval(async () => {
          try {
            await rollCallClick();
          } catch (error) {
            console.error("定时数据获取失败:", error);
            stopTracking();
            ElMessage.error(t("message.reaTimePlot.dataProcessError"));
          }
        }, interval * 1000);
      }
    } else if (trackForm.value.updateMode === 'auto') {
      trackTimer = setInterval(async () => {
        try {
          await sendMessage();
        } catch (error) {
          console.error("自动更新检查失败:", error);
        }
      }, 2000);
    }

    const durationMs = trackForm.value.durationValue * 60 * 1000;
    setTimeout(() => {
      if (isTracking.value) {
        stopTracking();
        ElMessage.success(t("message.reaTimePlot.trackingCompleted"));
      }
    }, durationMs);

    TrackDialogVisible.value = false;
    ElMessage.success(t("message.ClientProjectMonitorSystemMonitorParmater.trackingStarted"));
  } catch (error) {
    console.error("启动实时跟踪失败:", error);
    stopTracking();
  } finally {
    partDataIsRollCallLoading.value = false;
  }
};

// ===== 自动刷新 =====
const startAutoRefresh = () => {
  stopAutoRefresh();

  if (selectedVarNames.value.length > 0 && isDataReceiving.value) {
    sendMessage();
  }

  timer = setInterval(() => {
    if (selectedVarNames.value.length > 0 && isDataReceiving.value) {
      sendMessage();
    }
  }, refreshInterval);

  heartTimer = setInterval(() => {
    heart();
  }, refreshHeartInterval);
};

const stopAutoRefresh = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
  if (heartTimer) {
    clearInterval(heartTimer);
    heartTimer = null;
  }
};

const RealeaseSubscribe = async () => {
  if (!isConnected.value) return true;

  try {
    await stopSubscription();
    return true;
  } catch (err) {
    console.error("释放订阅失败:", err);
    ElMessage.error(t("message.reaTimePlot.releaseFailed"));
    return false;
  }
};

// ===== 计算属性 =====
const filteredVarDevices = computed(() => {
  const result = {};
  if (selectedVarNames.value.length === 0) {
    return props.varDevices;
  }
  for (const [deviceCode, varObjects] of Object.entries(props.varDevices)) {
    const filteredVars = varObjects.filter(varObj => 
      selectedVarNames.value.includes(varObj.name)
    );
    if (filteredVars.length > 0) {
      result[deviceCode] = filteredVars;
    }
  }
  return result;
});

// ===== 监听器 =====
watch(
  () => selectedVarNames.value,
  (newVal) => {
    if (newVal.length > 0 && isDataReceiving.value) {
      sendMessage();
    } else {
      stopSubscription();
    }
  },
  { deep: true }
);

watch(
  () => props.varDevices,
  (newVarDevices) => {
    if (Object.keys(newVarDevices).length > 0) {
      selectedVarNames.value = allVariables.value.map(v => v.name);
      updateDisplayVariables();
    }
  },
  { immediate: true, deep: true }
);

// ★ 从隐藏变可见时，主动 resize 兜底（ResizeObserver 在 0→有值边界有时不触发）
watch(
  () => props.visible,
  async (v) => {
    if (!v) return;
    await nextTick();
    // 再等一帧，确保 v-show 的 display 已切换、grid 布局已重排完成
    requestAnimationFrame(() => {
      if (chartInstance && chartRef.value && chartRef.value.offsetWidth > 0) {
        chartInstance.resize({ duration: 0 });
      }
    });
  }
);

// ===== 生命周期 =====
onMounted(async () => {
  await loadConfig();
  initChart();
  startAutoRefresh();
  await start();
  on("ReceiveMessage", handleNotification);
  await rollCallClick();
});

onBeforeUnmount(() => {
  // ★ 清理 ResizeObserver
  if (chartResizeObserver) {
    chartResizeObserver.disconnect();
    chartResizeObserver = null;
  }
  // ★ 取消未执行的 rAF
  if (resizeRafId) {
    cancelAnimationFrame(resizeRafId);
    resizeRafId = 0;
  }
  if (chartInstance) {
    chartInstance.dispose();
    chartInstance = null;
  }
  stopAutoRefresh();
});

onUnmounted(async () => {
  if (selectedVarNames.value.length > 0) {
    try {
      await RealeaseSubscribe();
      if (trackTimer) {
        clearInterval(trackTimer);
        trackTimer = null;
      }
    } catch (error) {
      console.error("组件卸载时释放订阅失败:", error);
    }
  }

  try {
    unsubscribe();
    off("ReceiveMessage", handleNotification);
    stop();
    stopTracking();
  } catch (error) {
    console.error("fail:", error);
  }
});

const unsubscribe = onConnected(() => {
  if (selectedVarNames.value.length > 0 && isDataReceiving.value) {
    sendMessage();
  }
  console.log("连接成功回调执行");
});
</script>

<style scoped>
.realtimeplot-container {
  padding: 12px;
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 16px;
  box-sizing: border-box;
}

.chart-container {
  background: #fff;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex: 1;
  min-height: 0;
  position: relative;
  overflow: hidden;
  width: 100%;
}

.chart {
  width: 100% !important;
  height: 100% !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
}

.chart-container .chart-toolbar {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 100;
}

.control-panel {
  background: #fff;
  padding: 12px 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  height: auto;
  min-height: fit-content;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.label-compact {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}

/* Y轴范围管理样式 */
.yaxis-range-list {
  padding: 16px;
}

.yaxis-range-item {
  margin-bottom: 20px;
  padding: 12px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
}

.range-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.variable-name {
  font-weight: bold;
  font-size: 14px;
  color: #303133;
}

.range-item-values {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 12px;
  color: #606266;
}

.range-item-actions {
  display: flex;
  gap: 8px;
}

/* 追踪对话框样式 */
.auto-update-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f0f9ff;
  border-radius: 4px;
  border: 1px solid #e1f5fe;
  color: #0288d1;
  font-size: 14px;
}

.duration-input {
  display: flex;
  align-items: center;
  gap: 8px;
}

.duration-unit {
  color: #666;
  font-size: 14px;
}

.duration-tips {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.custom-interval-input {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.interval-unit {
  color: #666;
  font-size: 14px;
  white-space: nowrap;
}

.tracking-status {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.remaining-time {
  font-size: 14px;
  color: #1890ff;
  font-weight: 500;
}

.current-interval {
  font-size: 13px;
  color: #52c41a;
  font-weight: 500;
}

.stop-btn {
  margin-right: auto;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .filter-row {
    flex-wrap: wrap;
  }
}

@media (max-width: 768px) {
  .realtimeplot-container {
    padding: 8px;
    gap: 12px;
  }
  
  .chart-container {
    padding: 8px;
  }
  
  .control-panel {
    padding: 10px;
  }
  
  .filter-row {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filter-row .el-select,
  .filter-row .el-date-picker,
  .filter-row .el-button {
    width: 100% !important;
  }
}

@media (max-width: 480px) {
  .realtimeplot-container {
    padding: 4px;
    gap: 8px;
  }
  
  .chart-container {
    padding: 4px;
  }
  
  .control-panel {
    padding: 8px;
  }
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-thumb {
  background: #c0c4cc;
  border-radius: 3px;
}

:deep(.el-table__body-wrapper)::-webkit-scrollbar-thumb:hover {
  background: #909399;
}

:deep(.el-table) {
  font-size: 14px;
}

:deep(.el-table .el-table__cell) {
  padding: 8px 0;
}

:deep(.el-table .cell) {
  padding: 0 12px;
  line-height: 1.4;
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

:deep(.echarts-for-vue .echarts-yAxis) {
  transition: none !important;
}

:deep(.echarts-for-vue .echarts-yAxis-line) {
  transition: none !important;
}

:deep(.echarts-for-vue .echarts-yAxis-label) {
  transition: none !important;
}

:deep(.echarts-for-vue .echarts-legend-item) {
  transition: none !important;
}

:deep(.echarts-for-vue .echarts-dataZoom-slider) {
  z-index: 1000 !important;
}
</style>